import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Sparkles, 
  Smartphone, 
  ArrowRight,
  CheckCircle2,
  Truck,
  Gift,
  Copy,
  DollarSign,
  Send,
  Percent,
  Inbox
} from 'lucide-react';
import './Offers.css';
import { getApiUrl } from '../../config/api';


const API_URL = getApiUrl('/api/offers');
const getToken = () => localStorage.getItem('access_token') || '';


const Offers = () => {
  const [discountType, setDiscountType] = useState('Percentage');
  const [valueAmount] = useState(15);
  const [optimization, setOptimization] = useState('Conversion Rate');
  const [headline, setHeadline] = useState('Here is 15% Off!');
  const [description] = useState('We noticed you eyeing those items. Use this code at checkout to save on your order.');
  const [buttonText] = useState('Apply Discount');
  const [promoCode] = useState('SAVE15');
  const [accentColor] = useState('#8B5CF6');
  const [triggers] = useState({ timeOnSite: true, exitIntent: false });
  const [targetSegments] = useState(['Returning Visitor']);
  const [isLaunched, setIsLaunched] = useState(false);

  const [globalPerformance, setGlobalPerformance] = useState(null);
  const [brainMetrics, setBrainMetrics] = useState(null);
  const [offersLoading, setOffersLoading] = useState(true);
  const [offersError, setOffersError] = useState(null);


  const fetchOffersData = useCallback(async () => {
    setOffersLoading(true);
    setOffersError(null);
    try {
      const token = getToken();
      const res = await fetch(API_URL, {
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const json = await res.json();

      setGlobalPerformance(json?.global_performance ?? null);
      setBrainMetrics(json?.brain_metrics ?? null);
    } catch (err) {
      console.error('Offers fetch error:', err);
      setOffersError('Unable to load live offer data from the server.');
      setGlobalPerformance(null);
      setBrainMetrics(null);
    } finally {
      setOffersLoading(false);
    }
  }, []);


  useEffect(() => {
    fetchOffersData();
  }, [fetchOffersData]);


  useEffect(() => {
    if (!isLaunched) {
      if (discountType === 'Percentage') setHeadline(`Here is ${valueAmount}% Off!`);
      else if (discountType === 'Fixed Amount') setHeadline(`Save $${valueAmount} Today!`);
      else setHeadline('Free Shipping Applied!');
    }
  }, [discountType, valueAmount, isLaunched]);


  const handleLaunch = () => {
    setIsLaunched(true);
    setTimeout(() => {
      setIsLaunched(false);
      fetchOffersData();
    }, 1500);
  };


  const renderGenerator = () => {
    const totalSent = globalPerformance?.total_sent ?? 0;
    const trendPct = globalPerformance?.trend_pct ?? 0;
    const weeklyDistribution = Array.isArray(globalPerformance?.weekly_distribution)
      ? globalPerformance.weekly_distribution
      : [];
    const hasWeeklyDistribution = weeklyDistribution.length > 0;

    return (
    <div className="row g-4">
      <div className="col-12 col-lg-8">
        {/* Builder Content */}
        <div className="builder-content-card">
          {isLaunched && (
            <div className="launch-overlay">
              <div className="launch-icon-wrapper">
                <CheckCircle2 size={32} />
              </div>
              <h3 className="launch-title">Offer Intelligence Active</h3>
              <p className="launch-subtitle">Offer deployed successfully</p>
            </div>
          )}

          <div className="builder-section-content">
              {/* Discount Type Section */}
              <section className="builder-section">
                <h4 className="builder-section-title">Discount Type</h4>
                <div className="row g-3">
                  {[
                    { id: 'Percentage', icon: <Percent size={24} strokeWidth={3} />, sub: 'Fixed % off' },
                    { id: 'Fixed Amount', icon: <DollarSign size={24} strokeWidth={3} />, sub: 'Hard $ value' },
                    { id: 'Free Ship', icon: <Truck size={24} strokeWidth={2.5} />, sub: 'Remove ship cost' }
                  ].map(type => (
                    <div key={type.id} className="col-12 col-md-4">
                      <button
                        onClick={() => setDiscountType(type.id)}
                        className={`discount-type-card ${discountType === type.id ? 'active' : ''}`}
                      >
                        <div className="discount-type-icon">{type.icon}</div>
                        <div className="discount-type-info">
                          <p className="discount-type-name">{type.id}</p>
                          <p className="discount-type-sub">{type.sub}</p>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </section>


              {/* Goal Optimization */}
              <section className="builder-section">
                <h4 className="builder-section-title">Goal Optimization</h4>
                <div className="optimization-pills">
                  {['Conversion Rate', 'AOV Growth', 'Clear Inventory'].map(opt => (
                    <button
                      key={opt}
                      onClick={() => setOptimization(opt)}
                      className={`optimization-pill ${optimization === opt ? 'active' : ''}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </section>


              {/* Global Performance Metrics — sourced from live API */}
              <section className="builder-section performance-section">
                <h4 className="builder-section-title">Global Performance Metrics</h4>
                {offersLoading ? (
                  <div className="offers-state-block">
                    <div className="loading-spinner"></div>
                    <p>Loading performance metrics...</p>
                  </div>
                ) : !globalPerformance ? (
                  <div className="offers-state-block">
                    <Inbox size={24} />
                    <p>{offersError || 'No performance data available yet'}</p>
                  </div>
                ) : (
                  <div className="performance-metric-card">
                    <div className="performance-metric-left">
                      <div className="performance-metric-icon">
                        <Send size={24} />
                      </div>
                      <div className="performance-metric-info">
                        <p className="performance-metric-label">Total Number of offers sent in 30 days</p>
                        <h3 className="performance-metric-value">
                          {totalSent.toLocaleString()}{' '}
                          <span className="performance-metric-trend">
                            {trendPct >= 0 ? '+' : ''}{trendPct}%
                          </span>
                        </h3>
                      </div>
                    </div>
                    {hasWeeklyDistribution && (
                      <div className="performance-chart">
                        {weeklyDistribution.map((h, i) => (
                          <div 
                            key={i} 
                            className={`performance-bar ${i === weeklyDistribution.length - 1 ? 'active' : ''}`}
                            style={{ height: `${h}%` }}
                          ></div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </section>

              <div className="launch-button-wrapper">
                <button onClick={handleLaunch} className="launch-engine-btn">
                  Launch Engine <ArrowRight size={20} strokeWidth={2.5} />
                </button>
              </div>
            </div>
        </div>
      </div>


      <div className="col-12 col-lg-4">
        <div className="preview-panel">
          <div className="preview-panel-header">
            <div className="preview-header-left">
              <Smartphone size={20} />
              <h3 className="preview-title">Deployment Preview</h3>
            </div>
            <div className="preview-dots">
              <span className="preview-dot red"></span>
              <span className="preview-dot yellow"></span>
              <span className="preview-dot green"></span>
            </div>
          </div>


          <div className="preview-display-area">
            <div className="preview-phone-mockup">
              <div className="preview-phone-header">
                <div className="preview-phone-icon">
                  <Gift size={26} strokeWidth={2.5} />
                </div>
                <span 
                  className="preview-phone-badge"
                  style={{ 
                    borderColor: `${accentColor}30`, 
                    color: accentColor, 
                    backgroundColor: `${accentColor}10` 
                  }}
                >
                  {targetSegments.length > 0 ? 'Dynamic' : 'Limited'}
                </span>
              </div>
              <h4 className="preview-phone-headline">{headline}</h4>
              <p className="preview-phone-desc">{description}</p>
              <div className="preview-phone-code">
                <span className="preview-phone-code-text">{promoCode}</span>
                <Copy size={16} />
              </div>
              <button 
                className="preview-phone-cta"
                style={{ 
                  backgroundColor: accentColor, 
                  boxShadow: `0 10px 25px -5px ${accentColor}40` 
                }}
              >
                {buttonText}
              </button>
            </div>
          </div>


          <div className="preview-recommendation">
            <div className="preview-rec-header">
              <Sparkles size={18} />
              <span>Neural Recommendation</span>
            </div>
            <p className="preview-rec-text">
              {triggers.exitIntent ? "System armed for Exit Intent trigger." : "Timed deployment active (30s delay)."} 
              Expected CR uplift: <span className="preview-rec-highlight">{brainMetrics?.conversion_uplift || '+0%'}</span>.
            </p>
          </div>
        </div>
      </div>
    </div>
    );
  };


  return (
    <>
      <Helmet>
        <title>Offer Engine - Revexia AI Revenue Brain</title>
        <meta name="description" content="AI-powered offer generation and dynamic decision engine" />
      </Helmet>


      <div className="offers-page-wrapper">
        <div className="container-fluid px-4">
          {/* Page Header */}
          <div className="offers-page-header">
            <div className="offers-header-info">
              <h2 className="offers-page-title">Campaign Hub</h2>
              <p className="offers-page-subtitle">Configure neural decision logic for real-time engagement</p>
            </div>
          </div>


          {/* Main Content */}
          <div className="offers-main-content">
            {renderGenerator()}
          </div>
        </div>
      </div>
    </>
  );
};


export default Offers;
